import { Router, NavModel } from 'aurelia-router';
import { I18N } from 'aurelia-i18n';
import { Account, Customer } from '../../models/customer';
import { State } from '../../services/global-state';
import { Business } from '../../models/business';
import { EventAggregator } from 'aurelia-event-aggregator';
import { mock, instance, when, anything, verify } from 'ts-mockito';
import { AutopayConfirmation } from './edit-autopay';
import { AutopayService } from './autopay.service';
import numeral from 'numeral';
import { FeeCalculationType } from '../../models/feeCalculationType';
import { SourcePayRazrApplication } from '../../models/sourcePayRazrApplication';
import { Ach, Autopay, AutopayFrequencyOption, AutopaySettings, Card, PaymentMethodType } from '../../models/autopay';
import { SetupAutopay } from './setup-autopay';
import { PayrazrService } from '../../services/payrazr';
import { HPFServiceClient } from '../../services/hpfApi';
import { Portfolio } from '../../models/portfolio';
import { ApiResponse } from '../../models/api-response';

describe('NewPortalWeb', () => {
	describe('pages', () => {
		describe('autopay', () => {
			describe('setupautopay', () => {
				let component: SetupAutopay;
				const mockCustomer: Customer = mock(Customer);
				const mockRouter: Router = mock(Router);
				let stubState: State;
				const mockI18N: I18N = mock(I18N);
				const mockEventAggregator: EventAggregator = mock(EventAggregator);
				const mockBusiness: Business = mock(Business);
				const mockAutoPayService: AutopayService = mock(AutopayService);
				const mockPayrazrService: PayrazrService = mock(PayrazrService);
				const mockHPFApi: HPFServiceClient = mock(HPFServiceClient);
				const mockAccount: Account = mock(Account);
				const mockPortfolio: Portfolio = mock(Portfolio);
				const mockAutopaySettings: AutopaySettings = mock(AutopaySettings);
				const mockAutopayFrequencyOption: AutopayFrequencyOption = mock(AutopayFrequencyOption);
				const mockAutoPay: Autopay = mock(Autopay);

				let cleanPaymentInformation = {
					isCardPayment: false,
					card: {
						selectedPaymentMethod: {
							cardInfoKey: 1
						},
						cardNumber: '1234',
						expMonth: 1,
						expYear: 2030,
						cvv: '123'
					},
					check: {
						selectedPaymentMethod: {
							checkInfoKey: 1
						},
						accountNumber: '123',
						transitNumber: '123',
						accountType: 'checking'
					},
					isThirdPartyPayment: true,
					name: 'Fake one',
					billingCity: 'Scottsdale',
					billingState: 'AZ',
					zipCode: '85001'
				};

				beforeEach(() => {
					stubState = new State(
						instance(mockI18N),
						instance(mockEventAggregator)
					);

					stubState.customer = instance(mockCustomer);
					stubState.business = instance(mockBusiness);
					const i18next = {
						t: (a, b) => a
					};
					when(mockI18N.i18next).thenReturn(i18next);
					spyOn(i18next, 't').and.returnValue('localized');

					component = new SetupAutopay(
						stubState,
						instance(mockRouter),
						instance(mockPayrazrService),
						instance(mockHPFApi),
						instance(mockAutoPayService),
						instance(mockI18N),
					);
				});
				it('should initialize SetupAutoPay component', () => {
					expect(component).toBeTruthy();
				});

				describe('getConfirmationTemplateData()', () => {
					it('should set confirmationTemplateData as a new TemplateData object with given values', () => {

						stubState.business = {
							portalSettings: {
								accountNumberLabel: 'Label'
							},
							name: 'Business Name',

							getMerchantKeyAssociatedToPortfolioByPortfolioName: () => { }
						};

						component.customer = instance(mockCustomer);
						component.account = instance(mockAccount);
						component.calculatedFeeAmount = 1;
						component.paymentInformation = {};
						component.paymentInformation.firstPaymentFee = 1;
						component.paymentInformation.autopay = {
							maxPaymentAmount: 1,
							payInFull: true,
							frequency: {
								description: 'NotSet'
							}
						};

						spyOn(stubState.business, 'getMerchantKeyAssociatedToPortfolioByPortfolioName').and.stub();

						component.getConfirmationTemplateData(anything());
						
						expect(component.confirmationTemplateData).not.toBe(null);
						expect(component.confirmationTemplateData.accountPayments).toBe(null);
						expect(component.confirmationTemplateData.feeValues).toEqual(anything());
					});
				});

				describe('showConfirmation()', () => {
					it('should set state as confirmation', () => {

						component.showConfirmation();

						expect(component.state).toBe('confirmation');
					});
				});

				describe('hideConfirmation()', () => {
					it('should set state as setup', () => {

						component.hideConfirmation();

						expect(component.state).toBe('setup');
					});
				});

				describe('configureFeeSettings()', () => {
					it('should set serviceFeeEnabled flag as allowFeesOnAutopays for merchant settings on portfolio', () => {

						component.portfolio = instance(mockPortfolio);
						component.portfolio.merchantSettings = {
							feeSettings: {
								allowFeesOnAutopays: true
							}
						}

						component.configureFeeSettings();

						expect(component.serviceFeeEnabled).toBe(component.portfolio.merchantSettings.feeSettings.allowFeesOnAutopays);
					});
				});

				describe('getFrequencies()', () => {
					it('should call hpfApi.getPortfolioAutopaySettings and set available frequencies', async () => {

						const apiResponse = new ApiResponse({
							isSuccessful: true,
							value: {
								autopayTriggerOptions: 1,
								selectableDaysOfMonth: 2
							}
						});

						component.portfolio = instance(mockPortfolio);

						when(mockHPFApi.getPortfolioAutopaySettings(anything())).thenResolve(apiResponse);

						await component.getFrequencies();

						verify(mockHPFApi.getPortfolioAutopaySettings(anything())).called();

						expect(component.availableFrequencies).toBe(apiResponse.value.autopayTriggerOptions);
						expect(component.daysOfMonth).toBe(apiResponse.value.selectableDaysOfMonth);
					});
				});

				describe('setMaxStartDate()', () => {
					it('should set maxFutureStartDateDays to portfolio.autopaySettings.maxFutureStartDateInDays', () => {

						component.portfolio = instance(mockPortfolio);
						component.portfolio.autopaySettings = instance(mockAutopaySettings);

						component.setMaxStartDate();

						expect(component.minFutureStartDateDays).toBe(0);
						expect(component.maxFutureStartDateDays).toBe(component.portfolio.autopaySettings.maxFutureStartDateInDays);
					});
				});

				describe('validate()', () => {

					const i18next = {
						t: (a, b) => a
					};

					beforeEach(() => {
						when(mockI18N.i18next).thenReturn(i18next);
						component.validationErrors = [];
						component.dayOfMonth = 0;
					});

					it('should not be any errors', () => {

						component.payInFull = true;
						component.selectedFrequency = instance(mockAutopayFrequencyOption);
						component.endDateOption = 'indefinite';

						component.validate();

						expect(component.validationErrors).not.toBe(null);
						expect(component.validationErrors.length).toBe(0);
					});

					it('should have error : Please select a payment amount', () => {
						const message = 'Please select a payment amount';
						component.payInFull = false;
						component.paymentAmount = '0';
						component.selectedFrequency = instance(mockAutopayFrequencyOption);
						component.endDateOption = 'indefinite';
						spyOn(i18next, 't').and.returnValue(message);
						component.validate();
						expect(component.validationErrors).not.toBe(null);
						expect(component.validationErrors.length).toBe(1);
						expect(component.validationErrors[0]).toBe(message);
					});

					it('should have error : Please select the frequency of autopay payments', () => {
						const message = 'Please select the frequency of autopay payments';
						component.payInFull = true;
						component.selectedFrequency = null;
						component.endDateOption = 'indefinite';
						spyOn(i18next, 't').and.returnValue(message);
						component.validate();
						expect(component.validationErrors).not.toBe(null);
						expect(component.validationErrors.length).toBe(1);
						expect(component.validationErrors[0]).toBe(message);
					});

					it('should have error : Please select a valid last payment date', () => {
						const message = 'Please select a valid last payment date';
						component.payInFull = true;
						component.selectedFrequency = instance(mockAutopayFrequencyOption);
						component.endDateOption = 'date';
						spyOn(i18next, 't').and.returnValue(message);
						component.validate();
						expect(component.validationErrors).not.toBe(null);
						expect(component.validationErrors.length).toBe(1);
						expect(component.validationErrors[0]).toBe(message);
					});

					it('should have error : Please select a valid number of payments', () => {
						const message = 'Please select a valid number of payments';
						component.payInFull = true;
						component.selectedFrequency = instance(mockAutopayFrequencyOption);
						component.endDateOption = 'payments';
						component.lastPaymentPayments = 0;
						spyOn(i18next, 't').and.returnValue(message);
						component.validate();
						expect(component.validationErrors).not.toBe(null);
						expect(component.validationErrors.length).toBe(1);
						expect(component.validationErrors[0]).toBe(message);
					});

					it('should have error : End date cannot be before first trigger date.', () => {
						const message = 'End date cannot be before first trigger date.';
						component.payInFull = true;
						component.selectedFrequency = {
							autopayTriggerType : 5,
							value: 0,
							description: '',
							planType: 0,
						};
						component.dayOfMonth = 8;
						const currentDate = new Date();
						component.lastPaymentDate = new Date(currentDate.getFullYear(), currentDate.getMonth() + 1, component.dayOfMonth - 1);
						component.endDateOption = 'date';
						spyOn(component, 'isLastDayOfMonth').and.returnValue(false);
						spyOn(i18next, 't').and.returnValue(message);
						component.validate();
						expect(component.validationErrors).not.toBe(null);
						expect(component.validationErrors.length).toBe(1);
						expect(component.validationErrors[0]).toBe(message);
						expect(component.isLastDayOfMonth).toHaveBeenCalled();
					});

				});

				describe('endDateOptionChanged()', () => {
					it('should set lastPaymentDate and lastPaymentPayments to null when endDateOption is indefinite', () => {
						component.endDateOption = 'indefinite';
						component.endDateOptionChanged();
						expect(component.lastPaymentDate).toBe(null);
						expect(component.lastPaymentPayments).toBe(null);
					});
					it('should set lastPaymentPayments to null when endDateOption is date', () => {
						component.endDateOption = 'date';
						component.endDateOptionChanged();
						expect(component.lastPaymentPayments).toBe(null);
					});
					it('should set lastPaymentDate to null when endDateOption is payments', () => {
						component.endDateOption = 'payments';
						component.endDateOptionChanged();
						expect(component.lastPaymentDate).toBe(null);
					});
				});

				describe('setSelectedEndOption()', () => {
					it('should set endDateOption as date when lastPaymentDate has value', () => {
						component.lastPaymentDate = new Date();
						component.lastPaymentPayments = null;
						component.setSelectedEndOption();
						expect(component.endDateOption).not.toBe(null);
						expect(component.endDateOption).toBe('date');
					});
					it('should set endDateOption as payments when lastPaymentPayments has value', () => {
						component.lastPaymentDate = null;
						component.lastPaymentPayments = 1;
						component.setSelectedEndOption();
						expect(component.endDateOption).not.toBe(null);
						expect(component.endDateOption).toBe('payments');
					});
					it('should set endDateOption as indefinite when lastPaymentDate and lastPaymentPayments has value', () => {
						component.lastPaymentDate = null;
						component.lastPaymentPayments = null;
						component.setSelectedEndOption();
						expect(component.endDateOption).not.toBe(null);
						expect(component.endDateOption).toBe('indefinite');
					});
				});

				describe('showError()', () => {
					it('should set error as the passed value', () => {
						const message = 'this is a error';
						component.showError(message);
						expect(component.error).not.toBe(null);
						expect(component.error).toBe(message);
					});
				});

				describe('reset()', () => {
					it('should reset the errors', () => {
						component.reset();
						expect(component.validationErrors).not.toBe(null);
						expect(component.validationErrors.length).toBe(0);
					});
				});

				describe('goBack()', () => {
					it('should change the route to secure', () => {
						component.goBack();
						verify(mockRouter.navigateToRoute('secure')).called();
					});
				});

				describe('getAutopayPaymentMethod()', () => {

					beforeEach(() => {
						stubState.customer = instance(mockCustomer);
						component.paymentInformation = cleanPaymentInformation;
					});

					it('should return Card as payment method from wallet', () => {
						component.paymentInformation.isCardPayment = true;
						var result = component.getAutopayPaymentMethod();
						expect(result).toBeInstanceOf(Card);
					});

					it('should return Card as payment method from details', () => {
						component.paymentInformation.isCardPayment = true;
						component.paymentInformation.card.selectedPaymentMethod = null;
						var result = component.getAutopayPaymentMethod();
						expect(result).toBeInstanceOf(Card);
					});

					it('should return ACH as payment method from wallet', () => {
						component.paymentInformation.isCardPayment = false;
						var result = component.getAutopayPaymentMethod();
						expect(result).toBeInstanceOf(Ach);
					});

					it('should return ACH as payment method from details', () => {
						component.paymentInformation.isCardPayment = false;
						component.paymentInformation.check.selectedPaymentMethod = null;
						var result = component.getAutopayPaymentMethod();
						expect(result).toBeInstanceOf(Ach);
					});

				});

				describe('submit()', () => {

					const i18next = {
						t: (a, b) => a
					};

					beforeEach(() => {

						when(mockI18N.i18next).thenReturn(i18next);
						stubState.customer = instance(mockCustomer);
						component.paymentInformation = cleanPaymentInformation;
					});

					it('should create an autopay and navigate to autopay-receipt', () => {
						component.account = instance(mockAccount);
						component.account.id = '1';
						component.paymentAmount = '10';
						component.payInFull = true;
						component.selectedFrequency = instance(mockAutopayFrequencyOption);
						component.dayOfMonth = 1;
						component.lastPaymentDate = new Date();
						component.lastPaymentPayments = 1;
						component.paymentInformation.isCardPayment = true;

						const apiResponse = new ApiResponse({
							isSuccessful: true,
							value: {
								autopayKey: 1
							}
						});

						component.autopayService.autopay = instance(mockAutoPay);
						component.autopayService.autopay.key = apiResponse.value.autopayKey;

						when(mockAutoPayService.createAutopay(anything())).thenResolve(apiResponse);

						component.submit();
						verify(mockAutoPayService.createAutopay(anything())).called();
					});

					it('should fail on create an autopay', async () => {
						component.account = instance(mockAccount);
						component.account.id = '1';
						component.paymentAmount = '10';
						component.payInFull = true;
						component.selectedFrequency = instance(mockAutopayFrequencyOption);
						component.dayOfMonth = 1;
						component.lastPaymentDate = new Date();
						component.lastPaymentPayments = 1;
						component.paymentInformation.isCardPayment = true;

						const apiResponse = new ApiResponse({
							isSuccessful: false
						});

						when(mockAutoPayService.createAutopay(anything())).thenResolve(apiResponse);

						await component.submit();
						verify(mockAutoPayService.createAutopay(anything())).called();
					});
				});

			});
		});
	});
});
